import { useEffect, useRef, useState } from 'react';
import { Button } from '../../components/form/button';
import { Typography } from '../../components/typography/typography';
import Icon from '../../components/icons/icon';
import classNames from 'classnames';
import { Product, ProductType } from '../../clients/quipli-service/product/product';
import { Location } from '../../clients/quipli-service/location/location';
import { OrderSection } from '../order/order-section/order-section';
import { Collapsible, CollapsibleRef } from '../../components/collapsible/collapsible';
import { Field, FieldLabel } from '../../components/field/field';
import { QuantityInput } from '../../customer/product-form/quantity-input/quantity-input';
import { PurchaseOrderProductLineItem } from '../../clients/quipli-service/purchase-order/purchase-order';
import { v4 as uuid } from 'uuid';
import { ProductAutoComplete } from '../../components/product-auto-complete/product-auto-complete';
import { useProductApiV1 } from '../../clients/quipli-service/product/product-api-v1';
import { toast } from '../../util/toasts';
import {
  GeneralLedgerAccount,
  GeneralLedgerAccountType,
  ItemGeneralLedgerTransactionType,
} from '../../clients/quipli-service/accounting/accounting';
import { useAccountingApiV1 } from '../../clients/quipli-service/accounting/accounting-api-v1';
import { usePurchaseOrderApiV1 } from '../../clients/quipli-service/purchase-order/purchase-order-api-v1';

export const PurchaseLineItemsForm = ({
  location,
  openOnStart = true,
  onAddItem,
}: {
  location?: Location;
  openOnStart?: boolean;
  onAddItem: (item: PurchaseOrderProductLineItem) => void;
}) => {
  const { getCustomProduct } = useProductApiV1();
  const { defaultPriceForProduct } = usePurchaseOrderApiV1();
  const { listAccounts, getItemGeneralLedgerAccount } = useAccountingApiV1();
  // States
  const [type, setType] = useState<ProductType>(ProductType.Product);
  const [customProduct, setCustomProduct] = useState<Product>();
  const [product, setProduct] = useState<Product | undefined>();
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>();
  const [price, setPrice] = useState<number>();
  const [qty, setQty] = useState<number>(1);
  const [accounts, setAccounts] = useState<GeneralLedgerAccount[]>();
  const [generalLedgerAccount, setGeneralLedgerAccount] = useState<GeneralLedgerAccount | undefined>();
  const [defaultGeneralLedgerAccount, setDefaultGeneralLedgerAccount] = useState<GeneralLedgerAccount | undefined>();

  const isDisabled = !price || !qty || (type === ProductType.Custom ? !name || !customProduct : !product);

  // Vars
  const collapsibleRef = useRef<CollapsibleRef>(null);

  // Callbacks
  const collapse = () => {
    collapsibleRef.current?.toggle(false);
  };

  const clear = () => {
    setProduct(undefined);
    setName('');
    setDescription(undefined);
    setPrice(undefined);
    setQty(1);
    setGeneralLedgerAccount(defaultGeneralLedgerAccount);
  };

  const submit = () => {
    if (type === ProductType.Service) return;
    if (isDisabled) return;

    const commonFields = {
      id: uuid(),
      quantity: qty,
      unitPrice: price,
    };

    if (type === ProductType.Custom) {
      onAddItem({
        name,
        description,
        generalLedgerAccountId: generalLedgerAccount?.id,
        product: customProduct!,
        ...commonFields,
      });
    } else {
      onAddItem({ ...commonFields, product: product! });
    }

    clear();
  };

  useEffect(() => {
    openOnStart && collapsibleRef.current?.toggle(true);
    listAccounts(false).then((res) => {
      const accountsToUse = res.filter((acc) => acc.accountType !== GeneralLedgerAccountType.Tax);
      setAccounts(accountsToUse);
      getItemGeneralLedgerAccount(undefined, undefined, ItemGeneralLedgerTransactionType.Expense).then((item) => {
        setGeneralLedgerAccount(accountsToUse.find((a) => a.id === item?.generalLedgerAccountId));
        setDefaultGeneralLedgerAccount(accountsToUse.find((a) => a.id === item?.generalLedgerAccountId));
      });
    });
  }, []);

  useEffect(() => {
    if (product) {
      defaultPriceForProduct(product.id).then((dp) => {
        if (dp) {
          setPrice(dp);
        } else if (product?.purchasePrice) {
          setPrice(product.purchasePrice);
        }
      });
    }
  }, [product]);

  useEffect(() => {
    getCustomProduct()
      .then(setCustomProduct)
      .catch((e) => {
        console.error(e);
        toast('Failed to fetch custom product', 'error');
      });
  }, []);

  return (
    <div className="order-add-product">
      <OrderSection>
        <Collapsible
          ref={collapsibleRef}
          trigger={(isOpen) => (
            <div className="flex justify-between items-center">
              <Typography type="text-md" weight="medium" className="text-grey-800">
                Add products to order
              </Typography>
              <Icon icon={isOpen ? 'Close' : 'Plus'} stroke="var(--quipli-black-800)" />
            </div>
          )}
        >
          <div className="flex flex-col gap-3">
            <div className="list-tabs">
              <div className="tabs-wrapper">
                <div
                  className={classNames('tab-item', { active: type === ProductType.Product })}
                  onClick={() => setType(ProductType.Product)}
                  data-cy="product_tab"
                >
                  <Typography
                    type="text-sm"
                    weight="medium"
                    className={type === ProductType.Product ? 'text-quipli-black-800' : 'text-grey-500'}
                  >
                    Product
                  </Typography>
                  <div className="divider-active"></div>
                </div>
                <div
                  className={classNames('tab-item', { active: type === ProductType.Part })}
                  onClick={() => {
                    setType(ProductType.Part);
                  }}
                  data-cy="part-tab"
                >
                  <Typography
                    type="text-sm"
                    weight="medium"
                    className={type === ProductType.Part ? 'text-quipli-black-800' : 'text-grey-500'}
                  >
                    Part
                  </Typography>
                  <div className="divider-active"></div>
                </div>
                <div
                  className={classNames('tab-item', { active: type === ProductType.Custom })}
                  onClick={() => {
                    setDescription(undefined);
                    setType(ProductType.Custom);
                  }}
                  data-cy="custom-tab"
                >
                  <Typography
                    type="text-sm"
                    weight="medium"
                    className={type === ProductType.Custom ? 'text-quipli-black-800' : 'text-grey-500'}
                  >
                    Other
                  </Typography>
                  <div className="divider-active"></div>
                </div>
              </div>
            </div>

            {type === ProductType.Custom ? (
              <CustomForm
                description={description}
                setDescription={setDescription}
                name={name}
                setName={setName}
                generalLedgerAccount={generalLedgerAccount}
                setGeneralLedgerAccount={setGeneralLedgerAccount}
                accounts={accounts || []}
              />
            ) : (
              <ProductAutoComplete
                types={[type]}
                onSelect={(p) => setProduct(p)}
                value={product}
                locationIds={location ? [location.id] : undefined}
              />
            )}

            <div className="flex flex-col md:flex-row gap-[12px] justify-between mb-3">
              <Field
                label="Price"
                isRequired
                type="currency"
                value={price}
                onChange={(v) => {
                  if (Number.isNaN(v) && !price) {
                    setPrice(NaN);
                  } else setPrice(v);
                }}
                maximumFractionDigits={2}
                dataCy="field_price"
              />

              <div className="">
                <FieldLabel label="Quantity" />
                <QuantityInput name="Quantity" value={`${qty}`} onChange={(v) => setQty(parseInt(v ?? '1'))} />
              </div>
            </div>

            <div className="button-group">
              <Button
                className="button-secondary"
                onClick={() => {
                  collapse();
                  clear();
                }}
                data-cy="cancel_add_product_to_order"
              >
                Cancel
              </Button>
              <Button className="button-primary" onClick={submit} disabled={isDisabled} data-cy="add_product_to_order">
                Add to order
              </Button>
            </div>
          </div>
        </Collapsible>
      </OrderSection>
    </div>
  );
};

const CustomForm = ({
  description,
  setDescription,
  name,
  setName,
  generalLedgerAccount,
  setGeneralLedgerAccount,
  accounts,
}: {
  description?: string;
  setDescription: (d?: string) => void;
  name: string;
  setName: (d: string) => void;
  generalLedgerAccount?: GeneralLedgerAccount;
  setGeneralLedgerAccount: (acc?: GeneralLedgerAccount) => void;
  accounts: GeneralLedgerAccount[];
}) => {
  return (
    <>
      <Field label="Name" isRequired type="text" value={name} onChange={(v) => setName(v)} dataCy="field_name" />
      <Field
        label="Description"
        type="text"
        value={description}
        onChange={(v) => setDescription(v)}
        dataCy="custom_field_description"
      />
      <Field
        type="dropdown"
        label="General Ledger Account"
        value={{
          label: generalLedgerAccount?.name,
          value: generalLedgerAccount?.id,
        }}
        dropdownProps={{ menuPortalTarget: document.body }}
        onChange={(v) => setGeneralLedgerAccount(accounts?.find((a) => a.id === v.value))}
        options={accounts?.map((a) => ({ label: a.name, value: a.id }))}
        dataCy="custom_field_gla"
      />
    </>
  );
};
